import { useMemo, useState } from 'react'
import { tryWords, type TryPage } from '../api'
import { mark } from '../lib/marking'
import { tryStore, today, type TryState } from '../lib/tryStore'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, Heading, Page, Progress } from '../components/ui'
import { Shell } from '../components/Shell'
import { ErrorScreen } from '../components/States'
import { Mascot } from '../components/Mascot'
import { Trial } from './trials'
import { say } from '../lib/speech'
import { sfx } from '../lib/sound'
import { t } from '../lib/i18n'

/**
 * Try Word Sprout, kept only on this device (29 Oct 2026; Sam: "what about
 * just saving the data to the user's device instead?").
 *
 * Before any account or consent. A grown-up picks the school year; the child
 * meets six words a day (their cards, then a question about each) and gets
 * "Finished for today". What they did is kept in this browser alone
 * (lib/tryStore.ts) and the server only ever hands out pages of words, the
 * same page to anybody (services/try_on_device.py). Why, and what a lawyer
 * must confirm: research/trial-and-consent/08-on-device-trial.md.
 *
 * The grown-up parts are in the family's language; the practice is English,
 * like every child screen.
 */
type Phase = 'setup' | 'ready' | 'run' | 'done'

export function TryIt({ onLeave, onSignUp }: { onLeave: () => void; onSignUp?: () => void }) {
  const [store, setStore] = useState<TryState>(() => tryStore.load())
  const [phase, setPhase] = useState<Phase>(() => (tryStore.load().year === null ? 'setup' : 'ready'))
  const [page, setPage] = useState<TryPage | null>(null)
  const [at, setAt] = useState(0)
  const [verdict, setVerdict] = useState<{ right: boolean; answer: unknown } | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [today_, setToday] = useState<{ met: number; missed: string[] }>({ met: 0, missed: [] })

  const doneToday = store.days.includes(today())
  const finishedAll = page !== null && page.words === 0

  const start = () => {
    if (store.year === null) return
    setError(null)
    tryWords(store.year, store.page)
      .then((p) => { setPage(p); setAt(0); setToday({ met: 0, missed: [] }); setPhase(p.words ? 'run' : 'done') })
      .catch(setError)
  }

  if (error) return <ErrorScreen error={error} onRetry={start} onHome={onLeave} grownUp />

  if (phase === 'setup') {
    return <Setup onPick={(year) => { setStore(tryStore.setYear(year)); setPhase('ready') }} onLeave={onLeave} />
  }

  if (phase === 'ready') {
    return (
      <Page width={560}>
        <Shell exitMode="home" onExit={onLeave}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Mascot size={72} mood="happy" label="" />
            <Heading sub={doneToday ? 'You did today\'s words. You can do more, or come back tomorrow.' : 'Six new words. Meet them, then use them.'}>
              {doneToday ? 'Finished for today' : "Today's words"}
            </Heading>
          </div>
          <Button full tone="child" onClick={start}>{doneToday ? 'Six more' : 'Start'}</Button>
          <DevicePanel store={store} onSignUp={onSignUp}
            onClear={() => { tryStore.clear(); setStore(tryStore.load()); setPhase('setup') }} />
        </Shell>
      </Page>
    )
  }

  if (phase === 'done' || !page) {
    return (
      <Page width={560}>
        <Shell exitMode="done" onExit={onLeave}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Mascot size={72} mood="cheer" label="" />
            <Heading sub={finishedAll
              ? 'That was every trial word for this year.'
              : `You met ${today_.met} words today. Come back tomorrow for the next ones.`}>
              Finished for today
            </Heading>
          </div>
          {today_.missed.length ? (
            <Card>
              <div style={{ fontWeight: 800, marginBottom: 8, color: theme.text }}>Words to look at again</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {today_.missed.map((w) => (
                  <button key={w} type="button" onClick={() => say(w)} style={{
                    fontFamily: 'inherit', fontSize: 16, fontWeight: 700, minHeight: TAP_MIN,
                    padding: '6px 12px', borderRadius: radius.sm, cursor: 'pointer',
                    border: `1.5px solid ${theme.border}`, background: theme.card, color: theme.text,
                  }}>🔊 {w}</button>
                ))}
              </div>
            </Card>
          ) : null}
          <div style={{ marginTop: 16 }}>
            <Button full tone="child" onClick={onLeave}>Done</Button>
          </div>
          <DevicePanel store={store} onSignUp={onSignUp}
            onClear={() => { tryStore.clear(); setStore(tryStore.load()); setPhase('setup') }} />
        </Shell>
      </Page>
    )
  }

  const q = page.questions[at]!
  const advance = () => {
    setVerdict(null)
    if (at + 1 < page.questions.length) { setAt(at + 1); return }
    setStore(tryStore.finishPage())
    sfx.finished()
    setPhase('done')
  }

  return (
    <Page width={560}>
      <Shell exitMode="home" onExit={onLeave}
        step={q.item_type === 'learning' ? 'Meet the word' : 'Use it'}>
        <div style={{ marginBottom: 18 }}>
          <Progress done={at} total={page.questions.length} />
        </div>
        {verdict ? (
          <Card style={{
            borderColor: verdict.right ? theme.green : theme.orange,
            background: verdict.right ? theme.greenPale : theme.orangePale,
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8,
              color: verdict.right ? theme.greenDarker : theme.orangeDark }}>
              {verdict.right ? 'Yes, that is it' : 'Not quite'}
            </div>
            {!verdict.right && q.key.about ? (
              <p style={{ margin: '0 0 12px', fontSize: 17, color: theme.text, lineHeight: 1.5 }}>
                The answer is <strong>{String(verdict.answer ?? q.key.about.word)}</strong>.{' '}
                {q.key.about.definition}
              </p>
            ) : null}
            <Button full tone="child" onClick={advance}>Next</Button>
          </Card>
        ) : (
          <Trial key={at} q={stripKey(q)} firstLanguage="en" busy={false}
            onAnswer={(response) => {
              const full = { ...q.payload, ...q.key }
              const correct = q.item_type === 'learning' ? null : mark(q.item_type, full, response)
              const word = q.key.about?.word ?? String(q.payload.word ?? '')
              tryStore.record(q.word_sense_id, word, correct)
              if (q.item_type === 'learning') setToday((d) => ({ ...d, met: d.met + 1 }))
              if (correct === false) setToday((d) => ({ ...d, missed: [...new Set([...d.missed, word])] }))
              if (correct === null) { advance(); return }
              if (correct) sfx.correct(); else sfx.notYet()
              setVerdict({ right: correct, answer: q.key.answer })
            }} />
        )}
      </Shell>
    </Page>
  )
}

function stripKey(q: TryPage['questions'][number]) {
  const { key: _key, word_sense_id: _id, ...shown } = q
  return shown
}

/** The grown-up's part: the school year, and what this trial is. */
function Setup({ onPick, onLeave }: { onPick: (year: number) => void; onLeave: () => void }) {
  const [year, setYear] = useState(4)
  return (
    <Page width={560}>
      <Shell exitMode="home" onExit={onLeave} grownUp>
        <Heading sub={t('Six words a day, for as long as you like. No account, and nothing your child does is sent to us.')}>
          {t('Try Word Sprout on this device')}
        </Heading>
        <Card>
          <label style={{ display: 'block', fontSize: 16, fontWeight: 700, color: theme.text, marginBottom: 8 }}>
            {t("Your child's school year")}
          </label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{
            fontFamily: 'inherit', fontSize: 17, padding: '10px 12px', minHeight: TAP_MIN,
            borderRadius: radius.sm, border: `1.5px solid ${theme.border}`, width: '100%',
          }}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((y) => (
              <option key={y} value={y}>{t('Year {n}', { n: y })}</option>
            ))}
          </select>
          <p style={{ margin: '14px 0 0', fontSize: 14, color: theme.muted, lineHeight: 1.55 }}>
            {t('What your child answers stays in this browser on this device. Word Sprout only sends the words for the school year you pick, the same words anyone gets. You can delete it all here at any time.')}
          </p>
        </Card>
        <div style={{ marginTop: 16 }}>
          <Button full onClick={() => onPick(year)}>{t('Hand it to my child')}</Button>
        </div>
      </Shell>
    </Page>
  )
}

/** For the grown-up, under the child's part: what is kept, delete it, sign up. */
function DevicePanel({ store, onClear, onSignUp }: { store: TryState; onClear: () => void; onSignUp?: () => void }) {
  const words = useMemo(() => Object.values(store.words), [store])
  return (
    <div style={{
      marginTop: 22, padding: '12px 14px', borderRadius: radius.sm,
      background: theme.blueFaint, border: `1px solid ${theme.blueTint}`, fontSize: 14,
      color: theme.text, lineHeight: 1.55,
    }}>
      <b>{t('For the grown-up')}</b>
      <div>{t('Kept on this device only: {words} words met. Nothing was sent to Word Sprout.',
        { words: words.length })}</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
        {onSignUp ? <Button tone="quiet" onClick={onSignUp}>{t('Make an account to keep going')}</Button> : null}
        <Button tone="ghost" onClick={() => {
          if (window.confirm(t('Delete everything this trial kept on this device?'))) onClear()
        }}>{t('Delete it')}</Button>
      </div>
    </div>
  )
}

